/**
 * SKOP Creator Profile Component
 *
 * Displays creator reputation and history.
 */

import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import {
  getCreatorProfile,
  getCreatorTokens,
  getCreatorNotes,
  saveCreatorNotes,
  blockCreator,
  unblockCreator,
  isCreatorBlocked,
  getRiskLevel,
  getRiskColor,
  BADGE_INFO,
  type CreatorProfile as CreatorProfileType,
  type CreatorToken,
  type CreatorBadge,
} from '../lib/creators';
import { shortenAddress } from '../lib/utils';

interface CreatorProfileProps {
  creatorAddress: string;
}

export function CreatorProfile({ creatorAddress }: CreatorProfileProps) {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString() ?? null;

  const [profile, setProfile] = useState<CreatorProfileType | null>(null);
  const [tokens, setTokens] = useState<CreatorToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTokens, setShowTokens] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');

  // Load creator data
  useEffect(() => {
    setLoading(true);
    Promise.all([
      getCreatorProfile(creatorAddress),
      getCreatorTokens(creatorAddress),
    ]).then(([profileData, tokensData]) => {
      setProfile(profileData);
      setTokens(tokensData);
      setLoading(false);
    });
  }, [creatorAddress]);

  // Load user-specific data
  useEffect(() => {
    if (walletAddress) {
      setIsBlocked(isCreatorBlocked(walletAddress, creatorAddress));
      const savedNotes = getCreatorNotes(walletAddress, creatorAddress);
      if (savedNotes) setNotes(savedNotes);
    }
  }, [walletAddress, creatorAddress]);

  const handleBlock = () => {
    if (!walletAddress) return;
    if (isBlocked) {
      unblockCreator(walletAddress, creatorAddress);
    } else {
      blockCreator(walletAddress, creatorAddress);
    }
    setIsBlocked(!isBlocked);
  };

  const handleSaveNotes = () => {
    if (!walletAddress) return;
    saveCreatorNotes(walletAddress, creatorAddress, notes);
    setShowNotes(false);
  };

  if (loading) {
    return (
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-lg)',
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
      }}>
        <div
          className="animate-spin"
          style={{
            width: '16px',
            height: '16px',
            border: '2px solid var(--border)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
          }}
        />
        <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
          Loading creator profile...
        </span>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-lg)',
        padding: '16px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
      }}>
        Creator profile not available
      </div>
    );
  }

  const riskLevel = getRiskLevel(profile.reputationScore);
  const riskColor = getRiskColor(riskLevel);

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '16px', height: '16px', display: 'inline-flex', color: 'var(--accent)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </span>
          <span style={{ fontWeight: 600 }}>Creator Profile</span>
        </div>
        {walletAddress && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowNotes(!showNotes)}
              style={{
                padding: '4px 8px',
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-secondary)',
                fontSize: '11px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Notes
            </button>
            <button
              onClick={handleBlock}
              style={{
                padding: '4px 8px',
                background: isBlocked ? 'rgba(255, 59, 59, 0.1)' : 'none',
                border: `1px solid ${isBlocked ? 'var(--error)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
                color: isBlocked ? 'var(--error)' : 'var(--text-secondary)',
                fontSize: '11px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
              </svg>
              {isBlocked ? 'Blocked' : 'Block'}
            </button>
          </div>
        )}
      </div>

      {/* Notes Editor */}
      {showNotes && (
        <div style={{
          padding: '12px 16px',
          background: 'var(--bg-tertiary)',
          borderBottom: '1px solid var(--border)',
        }}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add private notes about this creator..."
            style={{
              width: '100%',
              minHeight: '60px',
              padding: '8px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              resize: 'vertical',
              marginBottom: '8px',
            }}
          />
          <button
            onClick={handleSaveNotes}
            style={{
              padding: '6px 12px',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: '#000',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Save Notes
          </button>
        </div>
      )}

      {/* Blocked Warning */}
      {isBlocked && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(255, 59, 59, 0.1)',
          borderBottom: '1px solid rgba(255, 59, 59, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ width: '14px', height: '14px', display: 'inline-flex' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
            </svg>
          </span>
          <span style={{ fontSize: '13px', color: 'var(--error)' }}>
            You blocked this creator
          </span>
        </div>
      )}

      {/* Profile Content */}
      <div style={{ padding: '16px' }}>
        {/* Address and Reputation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px',
        }}>
          <div>
            <a
              href={`https://solscan.io/account/${creatorAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '13px',
                color: 'var(--accent)',
                textDecoration: 'none',
              }}
            >
              {shortenAddress(creatorAddress, 8)}
            </a>
            <div style={{
              display: 'flex',
              gap: '4px',
              marginTop: '8px',
              flexWrap: 'wrap',
            }}>
              {profile.badges.map((badge) => (
                <Badge key={badge} badge={badge} />
              ))}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 700,
              color: riskColor,
            }}>
              {profile.reputationScore}
            </div>
            <div style={{
              fontSize: '11px',
              color: riskColor,
              textTransform: 'uppercase',
            }}>
              {riskLevel} Risk
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '12px',
          marginBottom: '16px',
        }}>
          <StatBox
            label="Tokens Created"
            value={profile.totalTokensCreated.toString()}
          />
          <StatBox
            label="Rugs"
            value={profile.rugCount.toString()}
            color={profile.rugCount > 0 ? 'var(--error)' : 'var(--success)'}
          />
          <StatBox
            label="Success Rate"
            value={`${Math.round((profile.successfulTokens / profile.totalTokensCreated) * 100)}%`}
            color={profile.successfulTokens > profile.rugCount ? 'var(--success)' : 'var(--error)'}
          />
          <StatBox
            label="Avg Hold"
            value={`${profile.averageHoldTime.toFixed(0)}h`}
            color={profile.averageHoldTime > 24 ? 'var(--success)' : 'var(--warning)'}
          />
        </div>

        {/* Token History Toggle */}
        <button
          onClick={() => setShowTokens(!showTokens)}
          style={{
            width: '100%',
            padding: '12px',
            background: 'var(--bg-tertiary)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Token History ({tokens.length})</span>
          <span style={{
            transform: showTokens ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}>
            ▼
          </span>
        </button>

        {/* Token History */}
        {showTokens && tokens.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {tokens.slice(0, 5).map((token) => (
              <TokenRow key={token.address} token={token} />
            ))}
            {tokens.length > 5 && (
              <div style={{
                textAlign: 'center',
                padding: '8px',
                fontSize: '12px',
                color: 'var(--text-tertiary)',
              }}>
                +{tokens.length - 5} more tokens
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function Badge({ badge }: { badge: CreatorBadge }) {
  const info = BADGE_INFO[badge];

  return (
    <span
      title={info.description}
      style={{
        padding: '2px 8px',
        background: `${info.color}20`,
        borderRadius: 'var(--radius-sm)',
        color: info.color,
        fontSize: '10px',
        fontWeight: 600,
      }}
    >
      {info.label}
    </span>
  );
}

function StatBox({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div style={{
      padding: '8px',
      background: 'var(--bg-tertiary)',
      borderRadius: 'var(--radius-md)',
      textAlign: 'center',
    }}>
      <div style={{
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        marginBottom: '4px',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '16px',
        fontWeight: 600,
        color: color || 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  );
}

function TokenRow({ token }: { token: CreatorToken }) {
  const statusColors: Record<string, string> = {
    active: 'var(--accent)',
    graduated: 'var(--success)',
    rugged: 'var(--error)',
    dead: 'var(--text-tertiary)',
  };

  const statusLabels: Record<string, string> = {
    active: 'Active',
    graduated: 'Graduated',
    rugged: 'Rugged',
    dead: 'Dead',
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid var(--border)',
    }}>
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '2px',
        }}>
          <span style={{ fontWeight: 600, fontSize: '13px' }}>{token.symbol}</span>
          <span style={{
            padding: '2px 6px',
            background: `${statusColors[token.status]}20`,
            borderRadius: 'var(--radius-sm)',
            color: statusColors[token.status],
            fontSize: '10px',
            fontWeight: 600,
          }}>
            {statusLabels[token.status]}
          </span>
        </div>
        <div style={{
          fontSize: '11px',
          color: 'var(--text-tertiary)',
        }}>
          {token.platform} • {new Date(token.createdAt).toLocaleDateString()}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        {token.peakMarketCap && (
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Peak: ${(token.peakMarketCap / 1000).toFixed(0)}K
          </div>
        )}
        {token.status === 'rugged' && token.creatorSoldAt && (
          <div style={{ fontSize: '11px', color: 'var(--error)' }}>
            Sold after {token.creatorSoldAt.toFixed(0)}h
          </div>
        )}
      </div>
    </div>
  );
}
